/**
 * The "View property schedule" affordance in the Case-management panel (spec 04-GNT
 * §5.4): a small chart-icon button that opens the {@link ScheduleDialog} ("Property
 * development schedule"). It is enabled as soon as an evaluation case is selected — a
 * calculation is NOT required, since a not-yet-calculated case reads its schedule pre-run
 * through the on-demand POST (`{ configurations: { evaluation_case_id } }`, with the run's
 * `evaluation_case_overrides` when it has "This run only" picks); before a case
 * is picked it is disabled with the schedule-first hint (the disabled-Button-in-Tooltip
 * pattern mirrored from `eval-settings-panel.tsx`).
 *
 * Every dialog prop is threaded from the page's EXISTING `useCalcDetail` read (no new
 * fetch beyond the dialog's own payload request): the cross-session `staleness`, the
 * `hasResults` / re-run-in-place identity, and the committed override log split into the
 * date ops the dialog seeds from and the other ops it must preserve verbatim.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { Button, Tooltip } from '@carbon/react';
import { ChartColumn } from '@carbon/react/icons';
import { useStore } from '@tanstack/react-form';
import { useAtom, useSetAtom } from 'jotai';
import { useEval3Page } from './context';
import {
  useCalcDetail,
  useEvaluationCaseDetail,
  type WorkingConfigForm,
} from './hooks';
import { ScheduleDialog } from './schedule-dialog';
import {
  splitCommittedOverrides,
  type PreservedOverrideWire,
} from './schedule-dialog/edit-ops';
import { DIALOG_SYNCED_DATE_FIELDS } from './schedule-dialog/constants';
import type {
  ScheduleOverrideEntry,
  SlotOverrideRejection,
} from './schedule-dialog/types';
import {
  scheduleDialogOpenAtom,
  scheduleFocusSearchAtom,
} from './schedule-dialog/atoms';
import { scheduleOverrideRejectionsAtom } from './atoms';
import {
  isInventoryPackageOverrideEntry,
  isTypeCurveOverrideEntry,
  runScenarioOverrides,
} from './utils';
import type { OverrideFields, SlotOverrideLogEntry } from './types';
import styles from './view-property-schedule.module.scss';

/**
 * Disabled-state hint shown until an evaluation case is selected. Exported so the Properties
 * grid's date-cell schedule pencil shows the SAME hint (INS-12497) — the two triggers can't drift.
 */
export const NO_CASE_HINT =
  'Select an evaluation case to view its property development schedule.';

/**
 * Drops the Calculate rejections a dialog re-edit supersedes (INS-12497). The dialog commits
 * `individual` date ops keyed by the DPS `slot_id` (unique_slot_id) with snake wire field keys
 * — the SAME id/field space a {@link SlotOverrideRejection} carries — so a corrected milestone
 * clears its own stale "rejected" mark now rather than lingering until the next Calculate. (The
 * grid's own rows are keyed by bare UWI, a different space; see INS-12392.) Returns the same
 * reference when nothing changed (stable for React).
 *
 * @param {SlotOverrideRejection[]} rejections - The current per-cell rejections.
 * @param {PreservedOverrideWire[]} ops - The dialog's committed override ops for this session.
 * @return {SlotOverrideRejection[]} The surviving rejections.
 */
export function clearRejectionsForScheduleOps(
  rejections: readonly SlotOverrideRejection[],
  ops: readonly PreservedOverrideWire[]
): readonly SlotOverrideRejection[] {
  if (rejections.length === 0) {
    return rejections;
  }
  const edited = new Set<string>();
  for (const op of ops) {
    if (op.type !== 'individual') {
      continue;
    }
    const slotId = op.slot_id;
    const overrides = op.overrides;
    if (
      typeof slotId !== 'string' ||
      typeof overrides !== 'object' ||
      !overrides
    ) {
      continue;
    }
    for (const field of Object.keys(overrides)) {
      edited.add(`${slotId}:${field}`);
    }
  }
  if (edited.size === 0) {
    return rejections;
  }
  const next = rejections.filter(
    (rejection) => !edited.has(`${rejection.slotId}:${rejection.field}`)
  );
  return next.length === rejections.length ? rejections : next;
}

/**
 * Folds the working config's live per-slot date edits (`slotOverrides`, captured by the
 * Properties/Slot-view cell edits) into the schedule dialog's {@link ScheduleOverrideEntry}
 * shape, so the dialog can overlay them onto its base rows and reflect them LIVE — before the
 * page's single Evaluate. Only the enumerable `slot` / `ids` operations fold: a `filter`
 * (select-all) op carries no client-side row population to resolve against here, so those bulk
 * date edits still surface only after Calculate. Later ops win field-by-field, mirroring the BE's
 * later-wins `slot_overrides` resolution. Slots with no milestone-date edit produce no entry.
 *
 * The mirror of slot-view's `overlayScheduleDateOverrides` (dialog → grid), and not merged
 * with it because the two translate opposite directions between different in/out shapes.
 * Which fields cross is single-sourced in {@link DIALOG_SYNCED_DATE_FIELDS}, which also maps
 * each one to the dialog row column it lands in — `completionDate` is the one operation field
 * whose column is spelled differently (`modeledCompletionDate`).
 *
 * @param {SlotOverrideLogEntry[]} operations - The working config's ordered override log.
 * @return {ScheduleOverrideEntry[]} One individual entry per slot with a milestone-date edit.
 */
function deriveSlotDateOverrides(
  operations: SlotOverrideLogEntry[]
): ScheduleOverrideEntry[] {
  const bySlot = new Map<string, Record<string, string | null>>();
  const record = (slotId: string, overrides: OverrideFields) => {
    for (const [field, milestone] of Object.entries(
      DIALOG_SYNCED_DATE_FIELDS
    )) {
      const value = overrides[field as keyof OverrideFields];
      if (value !== undefined) {
        bySlot.set(slotId, {
          ...(bySlot.get(slotId) ?? {}),
          [milestone]: value as string | null,
        });
      }
    }
  };

  // ── A REVERT REMOVES, IT DOES NOT SET ─────────────────────────────────────────────────
  //
  // The `unset` operation matched neither branch below, fell through both, and recorded
  // nothing — so reverting a date left this schedule showing the value just reverted away,
  // while the grid and the tearsheet both dropped it. Deleting here mirrors
  // SlotOverrideResolver#resolve_unset: at this position the named fields are cleared, and
  // because the loop runs in log order a LATER set re-records them exactly as the resolver
  // lets a later entry win. Idempotent for the same reason the resolver's is — it deletes.
  //
  // Keyed off the SAME map `record` writes through, so an unset can clear exactly the fields
  // a set can record and no others — including `completionDate`, which the tearsheet can now
  // originate. A field outside the map is ignored rather than deleted
  // blind: `next` is keyed by milestone, so deleting the raw field name could only ever
  // remove a key that is not there, or — if the two spellings ever collided — the wrong one.
  const forget = (slotId: string, fields: string[]) => {
    const current = bySlot.get(slotId);
    if (!current) {
      return;
    }
    const next = { ...current };
    for (const field of fields) {
      const milestone = DIALOG_SYNCED_DATE_FIELDS[field];
      if (milestone) {
        delete next[milestone];
      }
    }
    // An entry whose last date was cleared must not linger as an empty override: the dialog
    // keys "this row is edited" off the entry's presence, so an empty one marks a row edited
    // with nothing in it.
    if (Object.keys(next).length === 0) {
      bySlot.delete(slotId);
    } else {
      bySlot.set(slotId, next);
    }
  };
  for (const operation of operations) {
    // A manager entry (`type_curve` / `inventory_package`) names an (area, formation),
    // not a slot — no dates to fold.
    if (
      isTypeCurveOverrideEntry(operation) ||
      isInventoryPackageOverrideEntry(operation)
    ) {
      continue;
    }
    if (operation.kind === 'unset') {
      forget(operation.slotId, operation.fields);
    } else if (operation.kind === 'slot') {
      record(operation.slotId, operation.overrides);
    } else if (operation.kind === 'ids') {
      for (const slotId of operation.slotIds) {
        record(slotId, operation.overrides);
      }
    }
    // `filter` ops carry no enumerated ids — skipped; those bulk date edits reflect post-Calculate.
  }
  return Array.from(bySlot.entries()).map(([slotId, overrides]) => ({
    type: 'individual',
    slotId,
    overrides,
  }));
}

export interface ViewPropertyScheduleProps {
  /** The selected evaluation case id (from the working-config form), or null when none. */
  evaluationCaseId: number | null;
  /**
   * The lifted working-config form handle. The dialog captures its per-slot date edits
   * into `scheduleSlotOverrides` via `setScheduleSlotOverrides`, so the eval page's SINGLE
   * main Evaluate carries them (INS-10671) — the dialog no longer runs its own calculate.
   */
  workingConfig: WorkingConfigForm;
}

/**
 * Renders the Case-management "View property schedule" button + the schedule dialog it
 * opens, gating the button on a selected evaluation case.
 *
 * @param {ViewPropertyScheduleProps} props - The selected evaluation case id (or null).
 * @return {JSX.Element} The affordance and its (closed-until-opened) dialog.
 */
export function ViewPropertySchedule({
  evaluationCaseId,
  workingConfig,
}: ViewPropertyScheduleProps) {
  const { calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  // Cached case read (5-min staleness; the Case-management panel warms the same key), used
  // only to name the dialog subtitle with the DPS scenario driving the schedule (Defect 6).
  const { data: caseDetail } = useEvaluationCaseDetail(evaluationCaseId);
  // Open-state is a shared atom (not local state) so the Properties grid's per-date-cell
  // affordance can open this dialog too (INS-12497) — the panel button and the grid icon
  // are the two triggers, both routed through the one flag. Reset on close.
  const [open, setOpen] = useAtom(scheduleDialogOpenAtom);
  const setFocus = useSetAtom(scheduleFocusSearchAtom);
  const setScheduleRejections = useSetAtom(scheduleOverrideRejectionsAtom);

  // The open/focus atoms are shared, so they outlive this component. Before this PR the open
  // state was local `useState` and reset itself on unmount; reproduce that so a route change or
  // the case-picker drill-in that swaps this out (`eval-settings-panel.tsx`) can't leave
  // `open === true` and pop the dialog by itself, with stale focus, on the next mount (INS-12497).
  useEffect(
    () => () => {
      setOpen(false);
      setFocus(null);
    },
    [setOpen, setFocus]
  );

  // Flush the dialog's committed date ops into the working config, and clear any Calculate
  // rejection a re-edited cell now supersedes (INS-12497) — the grid commit path already does
  // the latter, and the dialog is now the only place to re-edit a milestone.
  const handleScheduleOverridesChange = useCallback(
    (ops: PreservedOverrideWire[]) => {
      workingConfig.setScheduleSlotOverrides(ops);
      setScheduleRejections((prev) => clearRejectionsForScheduleOps(prev, ops));
    },
    [workingConfig, setScheduleRejections]
  );

  const caseSelected = evaluationCaseId !== null;

  // The run's "This run only" picks: a pre-run read schedules the case with them, as the run
  // would (DevelopmentPaceScenario::CaseSchedulePreview reads evaluation_case_overrides). Sent only
  // when there are any, so a run on the case as saved asks exactly as before.
  const caseOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseOverrides
  );
  const priceDeckId = useStore(
    workingConfig.form.store,
    (state) => state.values.priceDeckId
  );
  const draftConfigurations = useMemo(() => {
    if (calculationId !== null || evaluationCaseId === null) return undefined;
    const overrides = runScenarioOverrides(caseOverrides, priceDeckId);
    return {
      evaluation_case_id: evaluationCaseId,
      ...(Object.keys(overrides).length > 0
        ? { evaluation_case_overrides: overrides }
        : {}),
    };
  }, [calculationId, evaluationCaseId, caseOverrides, priceDeckId]);

  // The case's own Development Pace scenario, named in the dialog subtitle; null when the case
  // has none, so the subtitle falls back to the case label.
  const developmentPaceScenarioName = useMemo(
    () =>
      caseDetail?.scenarioDomains?.find(
        (entry) => entry.domain === 'development_pace'
      )?.scenarioName ?? null,
    [caseDetail]
  );

  // The working config's CURRENT schedule contribution — seeds the dialog's display on
  // reopen so prior uncommitted edits survive; read reactively so a fork pre-seed
  // (a saved eval) is reflected.
  const pendingScheduleOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.scheduleSlotOverrides
  );

  // The working config's LIVE per-slot date edits from the Properties/Slot-view grid
  // (`slotOverrides`), folded to schedule entries and threaded into the dialog so a grid date
  // edit (permit / spud / first production) reflects LIVE in the schedule chart + Table — before
  // the page's single Evaluate. Read reactively so a new cell edit updates an already-open dialog.
  const slotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.slotOverrides
  );
  const slotDateOverrides = useMemo(
    () => deriveSlotDateOverrides(slotOverrides ?? []),
    [slotOverrides]
  );

  // Seed the dialog from the committed ops-log the calc echoes: the individual date ops
  // become the editable baseline; every other op is preserved verbatim so a schedule
  // Calculate never drops the eval's bulk / filter / economic edits. Empty pre-run (no
  // calc → `useCalcDetail` is idle, so no committed log yet).
  const { committed, preserved } = useMemo(
    () => splitCommittedOverrides(calcDetail?.configurations?.slot_overrides),
    [calcDetail]
  );

  // Re-run the viewed eval in place — EXCEPT a saved eval (immutable snapshot) or a
  // pre-run draft (no row yet), which must fork a new eval. Mirrors `mustCreateNew` in
  // `eval-settings-panel.tsx`: an omitted id makes the dialog's Calculate create-new.
  const existingEvaluationId =
    calculationId !== null && !(calcDetail?.saved ?? false)
      ? calculationId
      : undefined;

  const button = (
    <Button
      kind="tertiary"
      size="sm"
      disabled={!caseSelected}
      renderIcon={ChartColumn}
      onClick={() => setOpen(true)}
    >
      View property schedule
    </Button>
  );

  return (
    <>
      {caseSelected ? (
        button
      ) : (
        // A disabled Button emits no hover, so a focusable span wraps it as the tooltip
        // trigger — the schedule-first hint stays discoverable on hover / focus.
        <Tooltip label={NO_CASE_HINT} align="top">
          <span className={styles.trigger} tabIndex={0}>
            {button}
          </span>
        </Tooltip>
      )}
      <ScheduleDialog
        open={open}
        onClose={() => setOpen(false)}
        scheduleComplete={caseSelected}
        hasResults={calcDetail?.status === 'Completed'}
        staleness={calcDetail?.staleness}
        existingEvaluationId={existingEvaluationId}
        committedOverrides={committed}
        preservedOverrides={preserved}
        onScheduleOverridesChange={handleScheduleOverridesChange}
        pendingScheduleOverrides={pendingScheduleOverrides}
        slotDateOverrides={slotDateOverrides}
        developmentPaceScenarioName={developmentPaceScenarioName}
        // Pre-run (no calc): the on-demand read is keyed on the picked case. With a calc,
        // the dialog reads the calc's frozen schedule and ignores this.
        draftConfigurations={draftConfigurations}
      />
    </>
  );
}
